/**
 * Клавиши поверх игр — мост к оболочке (`electron/keys.ts`).
 *
 * Сочетания собираются из нескольких мест: рацию назначает звонок, пока он
 * идёт в режиме «по кнопке», остальное — свои сочетания из настроек. Каждое
 * место пишет свой набор (`setGlobalBinds`), оболочке уходит общий список.
 * В вебе моста нет, и всё здесь молча ничего не делает.
 */
export type GlobalAction = ShellKeyAction;
export type GlobalKeysState = ShellKeysState;

export interface GlobalBind {
  action: GlobalAction;
  keys: string[];
}

const sources = new Map<string, GlobalBind[]>();
let queued = false;

/** Набор сочетаний от одного места; пустой — место больше ничего не слушает. */
export function setGlobalBinds(source: string, binds: GlobalBind[]): void {
  if (binds.length) sources.set(source, binds);
  else sources.delete(source);
  // Несколько мест меняются за один кадр — оболочке хватит одного списка.
  if (queued) return;
  queued = true;
  queueMicrotask(() => {
    queued = false;
    void window.airelShell?.keys.bind([...sources.values()].flat());
  });
}

/** Действие зажали или отпустили — где бы ни был фокус. */
export function onGlobalKey(handler: (action: GlobalAction, down: boolean) => void): () => void {
  return window.airelShell?.keys.onEvent((event) => handler(event.action, event.down)) ?? (() => {});
}

/** Что слушает оболочка; `null` — это веб. */
export async function globalKeysState(): Promise<GlobalKeysState | null> {
  return (await window.airelShell?.keys.state()) ?? null;
}

/** Назначали ли клавиши в системе: тогда портал открывается на каждом запуске. */
const PORTAL_KEY = "airel.keys.portal";

/**
 * Объявить действия порталу Wayland — KDE и GNOME покажут окно назначения.
 * Сеанс портала живёт, пока живёт приложение, и назначенные клавиши без него
 * молчат, поэтому согласие запоминается и сеанс открывается снова при запуске
 * (`restoreSystemKeys`). Повтор окна там не показывает: система помнит.
 */
export async function bindSystemKeys(): Promise<GlobalKeysState | null> {
  const state = (await window.airelShell?.keys.portal()) ?? null;
  try {
    if (state?.portal) localStorage.setItem(PORTAL_KEY, "1");
  } catch {
    // Хранилище закрыто — при следующем запуске назначат ещё раз.
  }
  return state;
}

/** При запуске: открыть портал снова, если клавиши в системе уже назначали. */
export function restoreSystemKeys(): void {
  try {
    if (localStorage.getItem(PORTAL_KEY) !== "1") return;
  } catch {
    return;
  }
  void window.airelShell?.keys.portal();
}
